import { ArrowLeft, FileType, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '../../../components/EmptyState.js';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import { useLicense } from '../../common/license-context.js';
import { CloneCustomForm } from '../custom-forms-migration/components/CloneCustomForm.js';
import { CustomForm } from '../custom-forms-migration/components/CustomForm.js';
import {
  MigrationStart,
  type MigrationAction,
} from '../custom-forms-migration/components/MigrationStart.js';
import type { WorkfrontWorkspaceOption } from '../custom-forms-migration/types.js';
import {
  buildWorkspaceError,
  buildWorkspaceOptions,
} from '../custom-forms-migration/workspace-options.js';
import { useWorkfrontObjectCatalog } from '../hooks.js';

/**
 * Workfront · Custom forms migration. Combines two surfaces:
 *
 *   - **Read-only catalog view** (default): lists every custom form
 *     the engine catalog has linked to at least one Fusion scenario.
 *     Backed by `useWorkfrontObjectCatalog()` — cached + paged.
 *
 *   - **Live migration wizard** (action-selector buttons): hits the
 *     POST `/v1/workfront/custom-forms` engine proxy to list / detail /
 *     clone / replace forms across workspaces. Clone creates a new
 *     form on the target; Replace PUTs over an existing target form
 *     (with optional backup). Both ported from
 *     `wf-audit/.../clone-custom-form/CloneCustomForm.tsx` +
 *     `wf-audit/.../custom-form/CustomForm.tsx`.
 *
 * Workspace dropdown reads live from `/v1/license` — primary + every
 * secondary workfront-config linked via
 * `license_secondary_workfront_configs`, ordered to match the engine's
 * `resolveWorkspace` so `secondary:<N>` refs resolve identically across
 * the round-trip.
 */

interface FormSummary {
  workfrontId: string;
  name: string;
  parentTypeLabel: string;
  scenarioReferences: number;
  workfrontLink: string | null;
}

/**
 * Builds the workspace dropdown options from `/v1/license`. Memoizes
 * the pure `buildWorkspaceOptions` derivation so the wizard + page
 * share the same `secondary:<index>` refs that the engine resolves
 * against — see `workspace-options.ts` for the contract + tests.
 */
function useWorkspaceOptions(): ReadonlyArray<WorkfrontWorkspaceOption> {
  const { details } = useLicense();
  return useMemo(() => buildWorkspaceOptions(details), [details]);
}

export function WorkfrontCustomFormsMigrationPage(): JSX.Element {
  const [migrationAction, setMigrationAction] = useState<MigrationAction | null>(null);
  const workspaceOptions = useWorkspaceOptions();

  // Both Clone + Replace need at least two workspaces (source ≠ target).
  // Surface the same warning string from the wizard side as the
  // landing-page button tooltip so the operator knows the gate up front.
  const workspaceError = buildWorkspaceError(workspaceOptions);
  const wizardDisabled = workspaceOptions.length < 2;

  if (migrationAction === 'clone' || migrationAction === 'replace') {
    const Wizard = migrationAction === 'clone' ? CloneCustomForm : CustomForm;
    return (
      <div className="relative z-10 flex h-full flex-col overflow-y-auto">
        <header className="flex flex-wrap items-center justify-between gap-3 px-6 pb-2 pt-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMigrationAction(null)}
            className="text-foreground/70 hover:text-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </header>
        <Wizard
          workspaceOptions={workspaceOptions}
          workspaceError={workspaceError}
          onCancel={() => setMigrationAction(null)}
        />
      </div>
    );
  }

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 px-6 pb-2 pt-6">
        <div>
          <h1 className="text-2xl font-light text-foreground">Custom forms migration</h1>
          <p className="text-sm text-foreground/60">
            Replace fields on an existing form or clone a form into a different workspace.
          </p>
        </div>
        <Link
          to="/workfront"
          className="text-sm text-foreground/70 underline hover:text-foreground"
        >
          ← Object catalog
        </Link>
      </header>
      <div className="flex flex-1 flex-col">
        <MigrationStart
          onSelect={setMigrationAction}
          replaceDisabled={wizardDisabled}
          replaceDisabledReason={workspaceError ?? undefined}
        />
        <CatalogPreview />
      </div>
    </div>
  );
}

/**
 * Read-only catalog preview — surfaces every form the engine catalog
 * detected. Kept as a reference below the wizard so operators can
 * eyeball which forms exist before kicking off a clone.
 */
function CatalogPreview(): JSX.Element {
  const { data: rows = [], isLoading, error } = useWorkfrontObjectCatalog();
  const [search, setSearch] = useState('');

  const forms = useMemo<ReadonlyArray<FormSummary>>(() => extractForms(rows), [rows]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return forms;
    return forms.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        f.parentTypeLabel.toLowerCase().includes(q) ||
        f.workfrontId.toLowerCase().includes(q),
    );
  }, [forms, search]);

  return (
    <section className="border-t border-foreground/10 px-6 py-6">
      <div className="flex flex-wrap items-center gap-3 pb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground/60">
          Custom forms detected by the engine catalog
        </h2>
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/40" />
          <Input
            type="search"
            placeholder="Search by form name, ID, or type…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-foreground/15 bg-foreground/5 pl-9 text-foreground placeholder:text-foreground/40 focus-visible:ring-white/30"
          />
        </div>
        <span className="ml-auto text-xs text-foreground/50">
          {filtered.length} of {forms.length} form{forms.length === 1 ? '' : 's'}
        </span>
      </div>

      {error ? (
        <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
          Could not load custom forms: {error.message}
        </div>
      ) : isLoading ? (
        <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        forms.length === 0 ? (
          <EmptyState
            icon={FileType}
            tint="sky"
            title="No custom forms detected"
            description="Custom forms appear here once the engine catalog finds an `objCode=CTGY` (or engine-labelled `Custom Form`) reference in any Fusion scenario."
          />
        ) : (
          <EmptyState
            icon={Search}
            title="No forms match your search"
            description={
              <>
                {forms.length} form{forms.length === 1 ? '' : 's'} hidden — try clearing the search
                box.
              </>
            }
          />
        )
      ) : (
        <div className="overflow-x-auto rounded-md border border-foreground/10 bg-black/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-foreground/10 text-xs uppercase tracking-wide text-foreground/40">
                <th className="px-3 py-2 text-left font-medium">Form</th>
                <th className="px-3 py-2 text-left font-medium">Parent object type</th>
                <th className="px-3 py-2 text-left font-medium">Workfront ID</th>
                <th className="px-3 py-2 text-right font-medium">Scenarios referencing</th>
              </tr>
            </thead>
            <tbody className="text-foreground/85">
              {filtered.map((form) => (
                <tr key={form.workfrontId} className="border-b border-foreground/5">
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-2">
                      <FileType className="h-4 w-4 text-foreground/40" />
                      {form.workfrontLink ? (
                        <a
                          href={form.workfrontLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground hover:underline"
                        >
                          {form.name}
                        </a>
                      ) : (
                        form.name
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs uppercase tracking-wide text-foreground/60">
                    {form.parentTypeLabel}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{form.workfrontId}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{form.scenarioReferences}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function extractForms(rows: ReadonlyArray<Record<string, unknown>>): ReadonlyArray<FormSummary> {
  return rows
    .filter((r) => {
      const t = r.workfrontType ?? r.workfrontTypeLabel ?? '';
      const tl = typeof t === 'string' ? t.toLowerCase() : '';
      return tl.includes('form') || tl === 'ctgy';
    })
    .map((r) => ({
      workfrontId: typeof r.workfrontId === 'string' ? r.workfrontId : '',
      name:
        (typeof r.workfrontName === 'string' && r.workfrontName) ||
        (typeof r.workfrontId === 'string' ? r.workfrontId : '—'),
      parentTypeLabel:
        (typeof r.workfrontTypeLabel === 'string' && r.workfrontTypeLabel) ||
        (typeof r.workfrontType === 'string' && r.workfrontType) ||
        '—',
      scenarioReferences: Array.isArray(r.fusionLinks) ? r.fusionLinks.length : 0,
      workfrontLink: typeof r.workfrontLink === 'string' ? r.workfrontLink : null,
    }))
    .sort((a, b) => b.scenarioReferences - a.scenarioReferences || a.name.localeCompare(b.name));
}
